import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { FullScreenProgress } from '@occc/ui-design-system';
import { snackbar } from '@occc-app/design-system';
import {
  RetailerNetworkLocationGroupFragment,
  useSiteManagementLocationUpdateMutation,
} from '@occc/graphql';

import { RetailerNetworkMyStoresStoreV2UpdatePage } from './retailer-network-my-stores-store-v2-update-page.component';
import { GeneralDetailsFormValues } from './retailer-network-my-stores-store-v2-update-page.types';
import {
  getGeneralDetailsInitialValues,
  getLocationGroupMutationChanges,
  prepareGeneralDetailsInputs,
} from './retailer-network-my-stores-store-v2-update-page.utils';

import {
  useGetRetailerNetworkLocationGroups,
  useGetRetailerNetworkLocations,
} from '../../data-access/queries';
import {
  useRetailerNetworkLocationGroupAddLocationsMutation,
  useRetailerNetworkLocationGroupRemoveLocationsMutation,
} from '../../data-access/mutations';
import { routeCreator } from '../retailer-connect.utils';
import { RetailerNetworkErrorMessage } from '../components/retailer-network-error-message.component';
import { RetailerNetworkNoData } from '../components/retailer-network-no-data.component';
import { useBreadcrumbLinks } from '../hooks/use-breadcrumb-links.hook';

export const RetailerNetworkMyStoresStoreV2UpdatePageContainer = () => {
  const navigate = useNavigate();
  const { networkId = '', locationId = '' } = useParams<{
    networkId: string;
    locationId: string;
  }>();
  const [searchParams] = useSearchParams();
  const groupRef = searchParams.get('groupRef');
  const groupName = searchParams.get('groupName');

  const { loading, data, error } = useGetRetailerNetworkLocations({
    filter: {
      retailerNetworkUuidEquals: networkId,
      retailerNetworkLocationUuidEquals: locationId,
    },
  });

  const location = data?.[0]?.location;
  const locationName = location?.name || location?.siteSystemConfiguration?.[0]?.identifier;

  const { data: locationGroups } = useGetRetailerNetworkLocationGroups({
    filter: {
      retailerNetworkUuidEquals: networkId,
      retailerNetworkLocationUuidEquals: locationId,
    },
  });
  const isLocationGroupsLoading = locationGroups === undefined;
  const currentLocationGroup = locationGroups?.[0] ?? null;

  const groupLabel = isLocationGroupsLoading ? groupName : currentLocationGroup?.name;
  const groupUuid = isLocationGroupsLoading ? groupRef : currentLocationGroup?.uuid;

  const groupSuffix =
    groupUuid && groupLabel
      ? `?groupRef=${groupUuid}&groupName=${encodeURIComponent(groupLabel)}`
      : '';

  const breadcrumbLinks = useMemo(
    () => [
      { label: 'My Stores', to: routeCreator.myStores(networkId) },
      ...(groupUuid && groupLabel
        ? [{ label: groupLabel, to: routeCreator.locationGroupDetails(networkId, groupUuid) }]
        : []),
      {
        label: locationName || 'Store Details',
        to: routeCreator.locationDetails(networkId, locationId) + groupSuffix,
      },
      { label: 'Edit General Details' },
    ],
    [locationName, networkId, locationId, groupUuid, groupLabel, groupSuffix],
  );

  useBreadcrumbLinks(breadcrumbLinks);

  const [isSaving, setIsSaving] = useState(false);

  const [locationUpdate] = useSiteManagementLocationUpdateMutation();
  const [addLocationToLocationGroup] = useRetailerNetworkLocationGroupAddLocationsMutation({
    refetchQueries: ['retailerNetworkLocationGroups'],
    awaitRefetchQueries: true,
  });
  const [removeLocationFromLocationGroup] = useRetailerNetworkLocationGroupRemoveLocationsMutation({
    refetchQueries: ['retailerNetworkLocationGroups'],
    awaitRefetchQueries: true,
  });

  const handleSubmit = useCallback(
    async (values: GeneralDetailsFormValues) => {
      const updateValues = prepareGeneralDetailsInputs(locationId, values);
      if (!updateValues) return;

      setIsSaving(true);

      try {
        const result = await locationUpdate({
          ...updateValues,
          refetchQueries: ['retailerNetworkLocationDetails'],
        });

        if (!result.data?.locationUpdate?.uuid) {
          setIsSaving(false);
          return;
        }

        const { locationGroupUuidsToRemove, locationGroupUuidToAdd } =
          getLocationGroupMutationChanges(
            locationGroups?.map(
              (locationGroup: RetailerNetworkLocationGroupFragment) => locationGroup.uuid,
            ),
            values.details.locationGroup?.uuid,
          );

        for (const locationGroupUuidToRemove of locationGroupUuidsToRemove) {
          await removeLocationFromLocationGroup({
            variables: {
              retailerNetworkUuid: networkId,
              locationGroupUuid: locationGroupUuidToRemove,
              locationUuids: [locationId],
            },
          });
        }

        if (locationGroupUuidToAdd) {
          await addLocationToLocationGroup({
            variables: {
              retailerNetworkUuid: networkId,
              locationGroupUuid: locationGroupUuidToAdd,
              locationUuids: [locationId],
            },
          });
        }

        navigate(routeCreator.locationDetails(networkId, locationId) + groupSuffix, {
          replace: true,
        });
        snackbar.success('Location was successfully updated');
      } catch {
        setIsSaving(false);
        snackbar.error('Unable to update location');
      }
    },
    [
      addLocationToLocationGroup,
      locationGroups,
      groupSuffix,
      locationId,
      locationUpdate,
      navigate,
      networkId,
      removeLocationFromLocationGroup,
    ],
  );

  const goBack = useCallback(() => {
    navigate(routeCreator.locationDetails(networkId, locationId) + groupSuffix, {
      replace: true,
    });
  }, [navigate, networkId, locationId, groupSuffix]);

  const initialValues = useMemo(
    () => getGeneralDetailsInitialValues(location, currentLocationGroup),
    [location, currentLocationGroup],
  );

  if (loading && !location) {
    return <FullScreenProgress />;
  }

  if (error) {
    return <RetailerNetworkErrorMessage />;
  }

  if (!location) {
    return <RetailerNetworkNoData />;
  }

  if (isSaving) {
    return <FullScreenProgress />;
  }

  return (
    <RetailerNetworkMyStoresStoreV2UpdatePage
      locationName={locationName ?? ''}
      retailerNetworkUuid={networkId}
      initialValues={initialValues}
      onSubmit={handleSubmit}
      goBack={goBack}
    />
  );
};
